// src/utils/historyUtils.js - Updated with Firebase
import { USER_TIERS } from '../services/UserService';
import featureAccess from './featureAccess';
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from '../firebase/firebaseConfig';

// References to auth getter
let currentUserGetter = null;

/**
 * Initialize the history utils with dependencies
 * @param {Function} getCurrentUser - Function to get the current user
 */
export const initializeHistoryUtils = (getCurrentUser) => {
  currentUserGetter = getCurrentUser;
};

/**
 * Get current user if available
 * @returns {Object|null} Current user or null
 */
const getCurrentUser = () => {
  if (currentUserGetter) {
    return currentUserGetter();
  }
  return null;
};

/**
 * Record a completed workout in the user's history
 * @param {Object} workout - The completed workout
 * @returns {Promise<boolean>} Success status
 */
export const recordWorkoutHistory = async (workout) => {
  const user = getCurrentUser();
  
  // If user is logged in with Firebase, save to Firestore
  if (user && user.uid) {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const workoutHistory = userData.workoutHistory || [];
        
        // Create workout with timestamp
        const workoutWithTimestamp = {
          ...workout,
          completedAt: new Date().toISOString()
        };
        
        const updatedHistory = [...workoutHistory, workoutWithTimestamp];
        
        await updateDoc(userDocRef, {
          workoutHistory: updatedHistory
        });
        
        return true;
      }
      
      // If user exists in Auth but not in Firestore, create document
      const workoutWithTimestamp = {
        ...workout,
        completedAt: new Date().toISOString()
      };
      
      await updateDoc(userDocRef, {
        workoutHistory: [workoutWithTimestamp]
      });
      
      return true;
    } catch (error) {
      console.error('Failed to record workout in Firestore:', error);
      // Fall back to localStorage
    }
  }
  
  // For non-authenticated users or fallback, use localStorage
  const userTier = USER_TIERS.NONE;
  
  // Check if user has access to workout history
  const historyString = localStorage.getItem('pilatesPulse_history');
  let history = [];
  
  if (historyString) {
    try {
      history = JSON.parse(historyString);
    } catch (error) {
      console.error('Failed to parse workout history:', error);
    }
  }
  
  const access = featureAccess.checkAccess('workoutHistory', history.length, userTier);
  if (!access.hasAccess) {
    console.warn('Guest users cannot record workout history');
    return false;
  }
  
  // Create workout with timestamp
  const workoutWithTimestamp = {
    ...workout,
    completedAt: new Date().toISOString()
  };
  
  // Save to localStorage
  try {
    // Check for history limit
    if (access.limitReached) {
      // If limit reached, remove oldest entry
      history.shift();
    }
    
    history.push(workoutWithTimestamp);
    localStorage.setItem('pilatesPulse_history', JSON.stringify(history));
    return true;
  } catch (error) {
    console.error('Failed to record workout history:', error);
    return false;
  }
};

/**
 * Get the workout history for the current user
 * @returns {Promise<Array>} Workout history
 */
export const getWorkoutHistory = async () => {
  const user = getCurrentUser();
  
  // If user is logged in with Firebase, get from Firestore
  if (user && user.uid) {
    try {
      const userDocRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        return userDoc.data().workoutHistory || [];
      }
      return [];
    } catch (error) {
      console.error('Failed to get workout history from Firestore:', error);
      // Fall back to localStorage
    }
  }
  
  // Get from localStorage (for guests or as fallback)
  try {
    const historyString = localStorage.getItem('pilatesPulse_history');
    return historyString ? JSON.parse(historyString) : [];
  } catch (error) {
    console.error('Failed to get workout history:', error);
    return [];
  }
};